import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import IrrfFolhaConciliacaoService, { IIrrfFolhaConciliacaoDecisao, IIrrfFolhaSugestaoPendente } from "../../services/IrrfFolhaConciliacaoService";

type UrlParam = {
    esteiraParam: string;
};

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

const FAIXA_SCORE_LABELS: Record<string, { label: string; bg: string }> = {
    MATCH_FORTE: { label: 'Match Forte', bg: '#198754' },
    MATCH_PROVAVEL: { label: 'Match Provável', bg: '#3f72ab' },
    MATCH_FRACO: { label: 'Match Fraco', bg: '#fd7e14' },
    NAO_CONCILIAR_AUTOMATICAMENTE: { label: 'Não Conciliar', bg: '#dc3545' },
};

const MODALIDADE_LABELS: Record<string, string> = {
    RECOLHIMENTO_CONSOLIDADO: 'Consolidado',
    RECOLHIMENTO_SEGREGADO: 'Segregado',
    RECOLHIMENTO_MISTO: 'Misto',
};

function Badge({ texto, cor }: { texto: string; cor: string }) {
    return (
        <span style={{
            display: 'inline-block', fontSize: '10px', fontWeight: 700,
            color: '#fff', background: cor, borderRadius: '3px',
            padding: '2px 8px', letterSpacing: '0.4px', textTransform: 'uppercase',
            whiteSpace: 'nowrap',
        }}>{texto}</span>
    );
}

function SugestaoCard({ sugestao, onAceitar, onRecusar, processando }: {
    sugestao: IIrrfFolhaSugestaoPendente;
    onAceitar: () => void;
    onRecusar: () => void;
    processando: boolean;
}) {
    const totalProvisoes = sugestao.lancamentos.filter(l => l.tipoLancamento === 'C').reduce((acc, l) => acc + l.valor, 0);
    const totalBaixas = sugestao.lancamentos.filter(l => l.tipoLancamento === 'D').reduce((acc, l) => acc + l.valor, 0);

    return (
        <div className="card mb-4" style={{ border: '1px solid #e4e6ef' }}>
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                        <div className="fw-bold">Provisões: {BRL.format(totalProvisoes)} &nbsp;|&nbsp; Baixas: {BRL.format(totalBaixas)}</div>
                        {sugestao.justificativa && <div className="text-muted mt-1" style={{ fontSize: '13px' }}>{sugestao.justificativa}</div>}
                    </div>
                    <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-success" disabled={processando} onClick={onAceitar}>Aceitar</button>
                        <button className="btn btn-sm btn-outline-danger" disabled={processando} onClick={onRecusar}>Recusar</button>
                    </div>
                </div>
                <table className="table table-sm mb-0">
                    <thead>
                        <tr>
                            <th>Conta</th>
                            <th>Tipo</th>
                            <th>Data</th>
                            <th>Histórico</th>
                            <th className="text-end">Valor</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sugestao.lancamentos.map(l => (
                            <tr key={l.id}>
                                <td>{l.contaEmpresa}</td>
                                <td>{l.tipoLancamento === 'C' ? 'Provisão' : 'Baixa'}</td>
                                <td>{moment(l.data).format('DD/MM/YYYY')}</td>
                                <td>{l.historico}</td>
                                <td className="text-end">{BRL.format(l.valor)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function IrrfFolhaConciliacao() {
    const { esteiraParam } = useParams<UrlParam>();
    const esteiraId = parseInt(esteiraParam) || 0;

    const [carregando, setCarregando] = useState<boolean>(true);
    const [sugestoes, setSugestoes] = useState<IIrrfFolhaSugestaoPendente[]>([]);
    const [decisoes, setDecisoes] = useState<IIrrfFolhaConciliacaoDecisao[]>([]);
    const [processandoChave, setProcessandoChave] = useState<string | null>(null);

    const carregar = useCallback(async () => {
        setCarregando(true);
        const [sugestoesResp, decisoesResp] = await Promise.all([
            IrrfFolhaConciliacaoService.listarSugestoesPendentes(esteiraId),
            IrrfFolhaConciliacaoService.listarDecisoes(esteiraId),
        ]);
        setSugestoes(sugestoesResp);
        setDecisoes(decisoesResp);
        setCarregando(false);
    }, [esteiraId]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const onAceitar = useCallback(async (chaveVinculoMatch: string) => {
        setProcessandoChave(chaveVinculoMatch);
        try {
            await IrrfFolhaConciliacaoService.aceitarSugestao(esteiraId, chaveVinculoMatch);
            Swal.fire({ text: 'Sugestão aceita com sucesso', timer: 3000, icon: 'success', showConfirmButton: false });
            setSugestoes(old => old.filter(s => s.chaveVinculoMatch !== chaveVinculoMatch));
        } catch (e: any) {
            Swal.fire({ text: e?.message || 'Falha ao aceitar a sugestão', icon: 'error' });
        } finally {
            setProcessandoChave(null);
        }
    }, [esteiraId]);

    const onRecusar = useCallback(async (chaveVinculoMatch: string) => {
        setProcessandoChave(chaveVinculoMatch);
        try {
            await IrrfFolhaConciliacaoService.recusarSugestao(esteiraId, chaveVinculoMatch);
            Swal.fire({ text: 'Sugestão recusada', timer: 3000, icon: 'success', showConfirmButton: false });
            setSugestoes(old => old.filter(s => s.chaveVinculoMatch !== chaveVinculoMatch));
        } catch (e: any) {
            Swal.fire({ text: e?.message || 'Falha ao recusar a sugestão', icon: 'error' });
        } finally {
            setProcessandoChave(null);
        }
    }, [esteiraId]);

    if (carregando) {
        return (
            <div className="row">
                <div className="col-md-12">
                    <span className="indicator-progress" style={{ display: 'block' }}>
                        Carregando conciliação de IRRF sobre Folha...{' '}
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="row">
            <div className="col-md-12">
                <h3 className="mb-4">Conciliação de IRRF sobre Folha de Pagamento</h3>

                <h5 className="mb-3">Sugestões pendentes de revisão ({sugestoes.length})</h5>
                {sugestoes.length === 0 ? (
                    <div className="text-muted mb-5">Nenhuma sugestão de IA pendente no momento.</div>
                ) : (
                    <div className="mb-5">
                        {sugestoes.map(s => (
                            <SugestaoCard
                                key={s.chaveVinculoMatch}
                                sugestao={s}
                                processando={processandoChave === s.chaveVinculoMatch}
                                onAceitar={() => onAceitar(s.chaveVinculoMatch)}
                                onRecusar={() => onRecusar(s.chaveVinculoMatch)}
                            />
                        ))}
                    </div>
                )}

                <h5 className="mb-3">Conciliado automaticamente ({decisoes.length})</h5>
                {decisoes.length === 0 ? (
                    <div className="text-muted">Nenhuma competência conciliada automaticamente ainda.</div>
                ) : (
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Competência</th>
                                <th>Modalidade</th>
                                <th>Provisões</th>
                                <th>Baixas</th>
                                <th>Score</th>
                                <th>Reason Codes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {decisoes.map(d => {
                                const faixa = FAIXA_SCORE_LABELS[d.faixaScore] ?? { label: d.faixaScore, bg: '#6c757d' };
                                return (
                                    <tr key={d.id}>
                                        <td>{String(d.mesCompetencia).padStart(2, '0')}/{d.anoCompetencia}</td>
                                        <td>{MODALIDADE_LABELS[d.modalidade] ?? d.modalidade}</td>
                                        <td>{d.quantidadeProvisoes}</td>
                                        <td>{d.quantidadeBaixas}</td>
                                        <td><Badge texto={`${faixa.label} (${d.score}%)`} cor={faixa.bg} /></td>
                                        <td style={{ fontSize: '12px' }}>{d.reasonCodes.join(', ')}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default IrrfFolhaConciliacao;
